/** Drag-to-resize for the docked chat column, mirroring the left sidebar's handle. */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { MouseEvent as ReactMouseEvent, RefObject } from 'react';

import { DOCK_DEFAULT_WIDTH } from './chatDockConstants';
import { useChatDockStore } from './useChatDockStore';

const resetBodyDragStyles = () => {
  document.body.style.cursor = '';
  document.body.style.userSelect = '';
};

/**
 * Drive the dock's left-edge resize handle.
 *
 * The dock grows leftward, so the new width is the aside's right edge minus the
 * pointer's x. The store's `setWidth` clamps to the dock bounds and persists per
 * surface, so this hook only owns the drag lifecycle. `isResizing` lets the
 * caller drop the width transition mid-drag — otherwise the column lags the cursor.
 *
 * @param asideRef - The dock's `<aside>`, measured for its right edge.
 * @returns Drag state plus the handle's mousedown and double-click (reset) handlers.
 */
export function useDockResize(asideRef: RefObject<HTMLElement | null>) {
  const setWidth = useChatDockStore((s) => s.setWidth);
  const [isResizing, setIsResizing] = useState(false);
  const cleanupRef = useRef<(() => void) | null>(null);

  const handleMouseDown = useCallback(
    (e: ReactMouseEvent) => {
      if (!asideRef.current) return;
      e.preventDefault();
      const right = asideRef.current.getBoundingClientRect().right;
      setIsResizing(true);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';

      const onMove = (ev: MouseEvent) => setWidth(right - ev.clientX);
      const onUp = () => {
        setIsResizing(false);
        cleanupRef.current?.();
      };
      cleanupRef.current = () => {
        resetBodyDragStyles();
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        cleanupRef.current = null;
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [asideRef, setWidth]
  );

  const handleDoubleClick = useCallback(() => setWidth(DOCK_DEFAULT_WIDTH), [setWidth]);

  // Unmounting mid-drag must not leave document listeners or a stuck cursor behind.
  useEffect(() => () => cleanupRef.current?.(), []);

  return { isResizing, handleMouseDown, handleDoubleClick };
}
